import { cn } from '@utils';
import { cva } from 'class-variance-authority';
import * as React from 'react';

export type IconBadgeSize = 'sm' | 'md' | 'lg' | 'xl';
export type IconBadgeVariant = 'accent' | 'primary' | 'secondary' | 'muted';

const iconBadgeVariants = cva('inline-flex items-center justify-center rounded-full', {
	variants: {
		size: {
			sm: 'h-7 w-7 [&>svg]:size-4',
			md: 'h-8 w-8 [&>svg]:size-5',
			lg: 'h-9 w-9 [&>svg]:size-6',
			xl: 'h-10 w-10 [&>svg]:size-7',
		},
		variant: {
			accent: 'bg-accent text-white',
			primary: 'bg-primary text-white',
			secondary: 'bg-secondary text-white',
			muted: 'bg-bgPrimary text-primary',
		},
	},
	defaultVariants: {
		size: 'md',
		variant: 'accent',
	},
});

export interface IIconBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
	size?: IconBadgeSize;
	variant?: IconBadgeVariant;
	children: React.ReactNode;
}

/**
 * Circular badge that centers an icon inside a colored disc.
 * Used for header decorations, empty-state hero icons, status markers, etc.
 */
const IconBadge = React.forwardRef<HTMLDivElement, IIconBadgeProps>(
	({ size = 'md', variant = 'accent', className, children, ...props }, ref) => (
		<div
			ref={ref}
			className={cn(iconBadgeVariants({ size, variant }), className)}
			data-testid="icon-badge"
			{...props}
		>
			{children}
		</div>
	)
);

IconBadge.displayName = 'IconBadge';

export { IconBadge };
